/**
 * mSTEM EXT-01 "Khai báo thiết bị mới" — 4-step device declaration wizard
 * (Phase 6 / Plan 06-02, MOCK-ONLY / DR-01). Presentational class component;
 * all wizard state lives in the container (src/containers/device-declaration-modal.jsx).
 *
 * Reuses the firmware-wizard <Modal> shell + the parameterized <StepIndicator>
 * (totalSteps={4}) verbatim — cross-phase consistency with the Phase-2 firmware
 * wizard. Icon "upload" is a native <input type=file> → FileReader data-URL
 * preview (client-side only, never uploaded; 100KB cap enforced in the container).
 *
 * React 16 class component (no hooks). onChange handlers bound via lodash.bindall
 * to avoid react/jsx-no-bind inline-arrow allocations. All user-facing strings
 * flow through react-intl under gen.ext.device.* (06-UI-SPEC §EXT-01).
 */
import bindAll from 'lodash.bindall';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {FormattedMessage, defineMessages, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import StepIndicator from '../firmware-wizard-modal/step-indicator.jsx';

import styles from './device-declaration-modal.css';

const messages = defineMessages({
    title: {
        defaultMessage: 'Khai báo thiết bị mới',
        description: 'Title of the device declaration wizard (EXT-01)',
        id: 'gen.ext.device.title'
    },
    step1: {
        defaultMessage: 'Thông tin',
        description: 'EXT-01 step 1 label — basic info',
        id: 'gen.ext.device.step1'
    },
    step2: {
        defaultMessage: 'Giao thức',
        description: 'EXT-01 step 2 label — protocol',
        id: 'gen.ext.device.step2'
    },
    step3: {
        defaultMessage: 'Ngoại vi',
        description: 'EXT-01 step 3 label — peripherals',
        id: 'gen.ext.device.step3'
    },
    step4: {
        defaultMessage: 'Xác nhận',
        description: 'EXT-01 step 4 label — review',
        id: 'gen.ext.device.step4'
    },
    nameLabel: {
        defaultMessage: 'Tên thiết bị',
        description: 'EXT-01 device name field label',
        id: 'gen.ext.device.nameLabel'
    },
    namePlaceholder: {
        defaultMessage: 'VD: MBF Robot v2',
        description: 'EXT-01 device name placeholder',
        id: 'gen.ext.device.namePlaceholder'
    },
    typeLabel: {
        defaultMessage: 'Loại thiết bị',
        description: 'EXT-01 device type dropdown label',
        id: 'gen.ext.device.typeLabel'
    },
    typeRobot: {
        defaultMessage: 'Robot',
        description: 'EXT-01 device type option — robot',
        id: 'gen.ext.device.type.robot'
    },
    typeKit: {
        defaultMessage: 'Kit SmartHome',
        description: 'EXT-01 device type option — smarthome kit',
        id: 'gen.ext.device.type.kit'
    },
    typeOther: {
        defaultMessage: 'Khác',
        description: 'EXT-01 device type option — other',
        id: 'gen.ext.device.type.other'
    },
    iconLabel: {
        defaultMessage: 'Biểu tượng (tùy chọn)',
        description: 'EXT-01 icon picker label',
        id: 'gen.ext.device.iconLabel'
    },
    iconUpload: {
        defaultMessage: 'Chọn ảnh…',
        description: 'EXT-01 icon upload button',
        id: 'gen.ext.device.iconUpload'
    },
    iconPreset: {
        defaultMessage: 'Hoặc chọn biểu tượng có sẵn',
        description: 'EXT-01 preset-icon row label',
        id: 'gen.ext.device.iconPreset'
    },
    protocolLabel: {
        defaultMessage: 'Giao thức giao tiếp',
        description: 'EXT-01 protocol radio group label',
        id: 'gen.ext.device.protocolLabel'
    },
    protocolWifi: {
        defaultMessage: 'Wi-Fi',
        description: 'EXT-01 protocol option — wifi',
        id: 'gen.ext.device.protocol.wifi'
    },
    protocolBluetooth: {
        defaultMessage: 'Bluetooth',
        description: 'EXT-01 protocol option — bluetooth',
        id: 'gen.ext.device.protocol.bluetooth'
    },
    protocolUsb: {
        defaultMessage: 'USB (UART)',
        description: 'EXT-01 protocol option — usb/uart',
        id: 'gen.ext.device.protocol.usb'
    },
    protocolSpi: {
        defaultMessage: 'SPI',
        description: 'EXT-01 protocol option — spi',
        id: 'gen.ext.device.protocol.spi'
    },
    protocolI2c: {
        defaultMessage: 'I2C',
        description: 'EXT-01 protocol option — i2c',
        id: 'gen.ext.device.protocol.i2c'
    },
    protocolParams: {
        defaultMessage: 'Tham số (tùy chọn)',
        description: 'EXT-01 optional protocol params field label',
        id: 'gen.ext.device.protocolParams'
    },
    peripheralsLabel: {
        defaultMessage: 'Chọn module ngoại vi',
        description: 'EXT-01 peripherals multi-select label',
        id: 'gen.ext.device.peripheralsLabel'
    },
    reviewTitle: {
        defaultMessage: 'Xác nhận thông tin thiết bị',
        description: 'EXT-01 step 4 review heading',
        id: 'gen.ext.device.reviewTitle'
    },
    next: {
        defaultMessage: 'Tiếp theo',
        description: 'EXT-01 wizard next button',
        id: 'gen.ext.device.next'
    },
    save: {
        defaultMessage: 'Lưu',
        description: 'EXT-01 wizard save button (step 4)',
        id: 'gen.ext.device.save'
    },
    back: {
        defaultMessage: 'Quay lại',
        description: 'EXT-01 wizard back button',
        id: 'gen.ext.device.back'
    },
    cancel: {
        defaultMessage: 'Hủy',
        description: 'EXT-01 wizard cancel button',
        id: 'gen.ext.common.cancel'
    },
    errName: {
        defaultMessage: 'Vui lòng nhập tên thiết bị.',
        description: 'EXT-01 validation — empty device name',
        id: 'gen.ext.device.errName'
    },
    errDup: {
        defaultMessage: 'Tên thiết bị này đã tồn tại. Hãy chọn tên khác.',
        description: 'EXT-01 validation — duplicate device name',
        id: 'gen.ext.device.errDup'
    },
    errIcon: {
        defaultMessage: 'Ảnh quá lớn (tối đa 100KB). Đã dùng biểu tượng mặc định.',
        description: 'EXT-01 validation — icon too large',
        id: 'gen.ext.device.errIcon'
    },
    none: {
        defaultMessage: '(không)',
        description: 'EXT-01 review — empty value placeholder',
        id: 'gen.ext.device.reviewNone'
    }
});

class DeviceDeclarationModalComponent extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleNameChange',
            'handleTypeChange',
            'handleIconChange',
            'handleProtocolChange',
            'handleProtocolParamsChange',
            'handlePeripheralChange'
        ]);
    }
    handleNameChange (e) {
        this.props.onName(e.target.value);
    }
    handleTypeChange (e) {
        this.props.onType(e.target.value);
    }
    handleIconChange (e) {
        this.props.onIcon(e.target.files && e.target.files[0]);
    }
    handlePeripheralChange (e) {
        // peripheral id carried on the checkbox value — avoids per-row .bind()
        this.props.onPeripheralToggle(e.target.value);
    }
    handleProtocolChange (e) {
        this.props.onProtocol(e.target.value);
    }
    handleProtocolParamsChange (e) {
        this.props.onProtocolParams(e.target.value);
    }
    renderNameError () {
        const {intl, nameError} = this.props;
        if (!nameError) return null;
        const msg = nameError === 'dup' ? messages.errDup : messages.errName;
        return (
            <div className={styles.errText}>
                {intl.formatMessage(msg)}
            </div>
        );
    }
    render () {
        const {
            deviceName,
            deviceType,
            iconDataUrl,
            iconError,
            intl,
            nameError,
            peripherals,
            peripheralsList,
            protocol,
            protocolParams,
            step,
            onBack,
            onNext,
            onRequestClose,
            onSave
        } = this.props;

        const stepLabels = [
            intl.formatMessage(messages.step1),
            intl.formatMessage(messages.step2),
            intl.formatMessage(messages.step3),
            intl.formatMessage(messages.step4)
        ];

        const protocolOptions = [
            {id: 'wifi', label: intl.formatMessage(messages.protocolWifi)},
            {id: 'bluetooth', label: intl.formatMessage(messages.protocolBluetooth)},
            {id: 'usb', label: intl.formatMessage(messages.protocolUsb)},
            {id: 'spi', label: intl.formatMessage(messages.protocolSpi)},
            {id: 'i2c', label: intl.formatMessage(messages.protocolI2c)}
        ];

        const typeLabelFor = t => {
            if (t === 'robot') return intl.formatMessage(messages.typeRobot);
            if (t === 'kit') return intl.formatMessage(messages.typeKit);
            if (t === 'other') return intl.formatMessage(messages.typeOther);
            return intl.formatMessage(messages.none);
        };
        const protocolLabelFor = p => {
            const found = protocolOptions.find(o => o.id === p);
            return found ? found.label : intl.formatMessage(messages.none);
        };
        const selectedPeripheralNames = peripherals
            .map(id => {
                const found = peripheralsList.find(pp => pp.id === id);
                return found ? found.name : id;
            });

        const deviceNameValid = Boolean(deviceName && deviceName.trim()) && nameError !== 'dup';
        const canAdvance =
            (step === 0 && Boolean(deviceNameValid && deviceType)) ||
            (step === 1 && Boolean(protocol)) ||
            step >= 2;

        return (
            <Modal
                className={styles.modalContent}
                contentLabel={intl.formatMessage(messages.title)}
                id="deviceDeclarationModal"
                onRequestClose={onRequestClose}
            >
                <div className={styles.body}>
                    <div className={styles.title}>
                        <FormattedMessage {...messages.title} />
                    </div>
                    <StepIndicator
                        currentStep={step}
                        labels={stepLabels}
                        totalSteps={4}
                    />
                    <div className={styles.stepContent}>
                        {step === 0 ? (
                            <div>
                                <label className={styles.fieldLabel}>
                                    <FormattedMessage {...messages.nameLabel} />
                                </label>
                                <input
                                    className={classNames(styles.input, {
                                        [styles.inputError]: Boolean(nameError)
                                    })}
                                    placeholder={intl.formatMessage(messages.namePlaceholder)}
                                    type="text"
                                    value={deviceName}
                                    onChange={this.handleNameChange}
                                />
                                {this.renderNameError()}

                                <label className={styles.fieldLabel}>
                                    <FormattedMessage {...messages.typeLabel} />
                                </label>
                                <select
                                    className={styles.select}
                                    value={deviceType}
                                    onChange={this.handleTypeChange}
                                >
                                    <option value="">{'—'}</option>
                                    <option value="robot">{intl.formatMessage(messages.typeRobot)}</option>
                                    <option value="kit">{intl.formatMessage(messages.typeKit)}</option>
                                    <option value="other">{intl.formatMessage(messages.typeOther)}</option>
                                </select>

                                <label className={styles.fieldLabel}>
                                    <FormattedMessage {...messages.iconLabel} />
                                </label>
                                <div className={styles.iconRow}>
                                    <label className={styles.iconButton}>
                                        <FormattedMessage {...messages.iconUpload} />
                                        <input
                                            accept="image/*"
                                            className={styles.fileInput}
                                            type="file"
                                            onChange={this.handleIconChange}
                                        />
                                    </label>
                                    {iconDataUrl ? (
                                        <img
                                            alt=""
                                            className={styles.iconPreview}
                                            src={iconDataUrl}
                                        />
                                    ) : null}
                                </div>
                                {iconError ? (
                                    <div className={styles.errText}>
                                        {intl.formatMessage(messages.errIcon)}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}

                        {step === 1 ? (
                            <div>
                                <label className={styles.fieldLabel}>
                                    <FormattedMessage {...messages.protocolLabel} />
                                </label>
                                <div className={styles.radioGroup}>
                                    {protocolOptions.map(opt => (
                                        <label
                                            key={opt.id}
                                            className={classNames(styles.radioRow, {
                                                [styles.radioSelected]: protocol === opt.id
                                            })}
                                        >
                                            <input
                                                checked={protocol === opt.id}
                                                name="ext-device-protocol"
                                                type="radio"
                                                value={opt.id}
                                                onChange={this.handleProtocolChange}
                                            />
                                            <span>{opt.label}</span>
                                        </label>
                                    ))}
                                </div>
                                <label className={styles.fieldLabel}>
                                    <FormattedMessage {...messages.protocolParams} />
                                </label>
                                <input
                                    className={styles.input}
                                    type="text"
                                    value={protocolParams}
                                    onChange={this.handleProtocolParamsChange}
                                />
                            </div>
                        ) : null}

                        {step === 2 ? (
                            <div>
                                <label className={styles.fieldLabel}>
                                    <FormattedMessage {...messages.peripheralsLabel} />
                                </label>
                                <div className={styles.checkGrid}>
                                    {peripheralsList.map(p => (
                                        <label
                                            key={p.id}
                                            className={classNames(styles.checkRow, {
                                                [styles.checkSelected]: peripherals.indexOf(p.id) !== -1
                                            })}
                                        >
                                            <input
                                                checked={peripherals.indexOf(p.id) !== -1}
                                                type="checkbox"
                                                value={p.id}
                                                onChange={this.handlePeripheralChange}
                                            />
                                            <span>{p.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        {step === 3 ? (
                            <div className={styles.review}>
                                <div className={styles.reviewTitle}>
                                    <FormattedMessage {...messages.reviewTitle} />
                                </div>
                                {iconDataUrl ? (
                                    <img
                                        alt=""
                                        className={styles.iconPreview}
                                        src={iconDataUrl}
                                    />
                                ) : null}
                                <div className={styles.reviewRow}>
                                    <span className={styles.reviewKey}>
                                        <FormattedMessage {...messages.nameLabel} />
                                    </span>
                                    <span className={styles.reviewVal}>
                                        {deviceName || intl.formatMessage(messages.none)}
                                    </span>
                                </div>
                                <div className={styles.reviewRow}>
                                    <span className={styles.reviewKey}>
                                        <FormattedMessage {...messages.typeLabel} />
                                    </span>
                                    <span className={styles.reviewVal}>{typeLabelFor(deviceType)}</span>
                                </div>
                                <div className={styles.reviewRow}>
                                    <span className={styles.reviewKey}>
                                        <FormattedMessage {...messages.protocolLabel} />
                                    </span>
                                    <span className={styles.reviewVal}>{protocolLabelFor(protocol)}</span>
                                </div>
                                <div className={styles.reviewRow}>
                                    <span className={styles.reviewKey}>
                                        <FormattedMessage {...messages.peripheralsLabel} />
                                    </span>
                                    <span className={styles.reviewVal}>
                                        {selectedPeripheralNames.length ?
                                            selectedPeripheralNames.join(', ') :
                                            intl.formatMessage(messages.none)}
                                    </span>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <div className={styles.footer}>
                        <button
                            className={styles.btnSecondary}
                            type="button"
                            onClick={onRequestClose}
                        >
                            <FormattedMessage {...messages.cancel} />
                        </button>
                        {step > 0 ? (
                            <button
                                className={styles.btnSecondary}
                                type="button"
                                onClick={onBack}
                            >
                                <FormattedMessage {...messages.back} />
                            </button>
                        ) : null}
                        {step < 3 ? (
                            <button
                                className={styles.btnPrimary}
                                disabled={!canAdvance}
                                type="button"
                                onClick={onNext}
                            >
                                <FormattedMessage {...messages.next} />
                            </button>
                        ) : (
                            <button
                                className={styles.btnPrimary}
                                type="button"
                                onClick={onSave}
                            >
                                <FormattedMessage {...messages.save} />
                            </button>
                        )}
                    </div>
                </div>
            </Modal>
        );
    }
}

DeviceDeclarationModalComponent.propTypes = {
    deviceName: PropTypes.string.isRequired,
    deviceType: PropTypes.string.isRequired,
    iconDataUrl: PropTypes.string,
    iconError: PropTypes.bool,
    intl: intlShape.isRequired,
    nameError: PropTypes.oneOf(['name', 'dup', null]),
    onBack: PropTypes.func.isRequired,
    onIcon: PropTypes.func.isRequired,
    onName: PropTypes.func.isRequired,
    onNext: PropTypes.func.isRequired,
    onPeripheralToggle: PropTypes.func.isRequired,
    onProtocol: PropTypes.func.isRequired,
    onProtocolParams: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onType: PropTypes.func.isRequired,
    peripherals: PropTypes.arrayOf(PropTypes.string).isRequired,
    peripheralsList: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string
    })).isRequired,
    protocol: PropTypes.string,
    protocolParams: PropTypes.string,
    step: PropTypes.number.isRequired
};

export default injectIntl(DeviceDeclarationModalComponent);
